import { useState, useCallback } from 'react';
import { runValidators } from '../utils/validators';

export const useFormValidation = (schema) => {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = useCallback(
    (name, value) => {
      if (!schema[name]) return null;
      const error = runValidators(value, schema[name]);
      setErrors((prev) => ({ ...prev, [name]: error }));
      return error;
    },
    [schema]
  );

  const touchField = useCallback(
    (name, value) => {
      setTouched((prev) => ({ ...prev, [name]: true }));
      return validateField(name, value);
    },
    [validateField]
  );

  const validateAll = useCallback(
    (formData) => {
      const newErrors = {};
      let isValid = true;
      Object.keys(schema).forEach((name) => {
        const error = runValidators(formData[name], schema[name]);
        if (error) {
          newErrors[name] = error;
          isValid = false;
        }
      });
      setErrors(newErrors);
      setTouched(Object.keys(schema).reduce((acc, k) => ({ ...acc, [k]: true }), {}));
      return isValid;
    },
    [schema]
  );

  const clearErrors = useCallback(() => {
    setErrors({});
    setTouched({});
  }, []);

  /** Merge Django/DRF-style field errors into state and mark fields touched. */
  const mergeServerFieldErrors = useCallback((fieldMap) => {
    if (!fieldMap || typeof fieldMap !== 'object') return;
    setErrors((prev) => ({ ...prev, ...fieldMap }));
    setTouched((prev) => {
      const next = { ...prev };
      Object.keys(fieldMap).forEach((k) => {
        next[k] = true;
      });
      return next;
    });
  }, []);

  const getFieldProps = useCallback(
    (name, value, onChange) => ({
      error: touched[name] && !!errors[name],
      helperText: touched[name] && errors[name] ? errors[name] : undefined,
      onBlur: () => touchField(name, value),
      onChange: (e) => {
        onChange(e);
        if (touched[name]) validateField(name, e.target.value);
      },
    }),
    [errors, touched, touchField, validateField]
  );

  return {
    errors,
    touched,
    validateField,
    touchField,
    validateAll,
    clearErrors,
    mergeServerFieldErrors,
    getFieldProps,
  };
};
